"use client";

import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { addMonths, format, subMonths } from "date-fns";
import { Button } from "@/shadcnComponents/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shadcnComponents/ui/select";
import { Calendar } from "./calendar";
import { useBrand } from "@/Providers/Brand";

type Events = {
  id: string;
  title: string;
  type: string;
  date: string;
  selected: number;
  live: number;
  status: "active" | "scheduled" | "completed";
};

export function CalendarDashboard() {
  const [date, setDate] = React.useState(new Date());
  const brandState = useBrand();
  const [events, setEvents] = React.useState<Events[]>([]);
  const [filteredEvents, setFilteredEvents] = React.useState<Events[]>([]);
  const [selectedStatus, setSelectedStatus] =
    React.useState<string>("all-status");

  React.useEffect(() => {
    const getLiveAndScheduledCollab = async () => {
      const result = await brandState.getLiveAndScheduledCollab();
      if (result?.success) {
        setEvents(result.events);
        setFilteredEvents(result.events);
      } else {
        console.error("Error fetching live and scheduled collab");
      }
    };
    getLiveAndScheduledCollab();
  }, [brandState.getLiveAndScheduledCollab]); // Added brandState.getLiveAndScheduledCollab to dependencies

  const handleStatusChange = (value: string) => {
    setSelectedStatus(value);
    if (value === "all-status") {
      setFilteredEvents(events);
    } else {
      const filtered = events.filter((event) => event.status === value);
      setFilteredEvents(filtered);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-white ">
      <div className="flex-1 space-y-4 p-4 md:p-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setDate(subMonths(date, 1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="text-lg font-semibold">
                {format(date, "MMMM yyyy")}
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setDate(addMonths(date, 1))}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Select value={selectedStatus} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-status">All</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Calendar date={date} events={filteredEvents} />
      </div>
    </div>
  );
}
